// Single source of truth for the self-serve Agency Tools plans.
//
// Both the pricing cards (AgencyPricingPlans) and the feature comparison
// table (PricingComparisonTable) read these definitions so the two can
// never drift apart. This data is authoritative for what we DISPLAY and
// must mirror `supabase/functions/_shared/razorpay-agency-plans.ts` (the
// server-side source of truth for what Razorpay actually charges).
//
// Kept in its own non-component module so Vite Fast Refresh stays happy
// (component files should only export components).

export type Cycle = 'monthly' | 'yearly';

// Feature switch for the monthly/yearly toggle. HIDDEN from regular
// users — the marketing surfaces (pricing cards, comparison table,
// pricing FAQ, in-app billing tab) all render monthly-only while this
// is false. The Razorpay yearly plan IDs in razorpay-agency-plans.ts
// stay provisioned, and existing yearly subscribers (if any) still
// render correctly on their billing tab (the AgencyBillingTab cycle
// default reads from their subscription row, not this flag). To
// re-enable yearly publicly, flip this AND the matching flag in
// AgencyBillingTab.tsx — they share the same gate.
export const YEARLY_ENABLED = false;

// Credits granted to a new agency for the one-time 7-day free trial.
// Single source of truth for every UI surface that mentions the free
// allowance (pricing cards, FAQ, in-app billing tab, welcome banner).
//
// The ACTUAL allowance lives in the trial subscription's credits_per_period
// (seeded as 5000 by tg_start_agency_trial_on_insert in
// supabase/migrations/...trial_credits_5000.sql) — SQL can't import this
// constant, so that literal must be kept in sync by hand. If you change this
// number, write a new migration to match.
export const FREE_TIER_CREDITS = 5000;

export interface TierCard {
  slug: 'starter' | 'growth' | 'scale';
  name: string;
  tagline: string;
  monthly_usd: number;
  yearly_usd: number;
  /**
   * Feature bullets rendered on the pricing card, top-to-bottom.
   * Single source of truth — the Product Hunt landing page and the
   * /pricing page read THIS list, so the two stay aligned. Only list
   * what actually ships today; phantom features (custom CRM hooks,
   * multi-domain embeds, etc.) get cut, not promised.
   */
  highlights: string[];
  highlight?: boolean;
  ctaLabel: string;
  /** When set, the card surfaces a secondary CTA above the primary one
   *  that routes the user into the 7-day free trial flow (no payment).
   *  Currently lives only on the Starter tile — the trial is "try
   *  Starter for free" semantically, so it's offered there instead of
   *  as a standalone tile. */
  trialCtaLabel?: string;
  /** When set, clicking the CTA routes to this href instead of the
   *  embed-inquiry modal. Used for the future signup flow. */
  ctaHref?: string;
}

// Note: there is no standalone Free / 7-day trial tier in this array.
// Earlier versions had one; we collapsed it into Starter because the
// trial is semantically "try Starter for 7 days" — keeping it as a
// separate tile created a phantom plan that no one actually subscribes
// to. The trial CTA now lives ON the Starter card via trialCtaLabel.
//
// Enterprise was also removed — it was a "contact us" tile with no
// matching Razorpay plan (razorpay-agency-plans.ts excludes 'enterprise'
// from AGENCY_PLANS) and the promises on it ("native CRM", "per-
// workspace branding") don't ship today. The pricing page's closing
// CTA still surfaces a "Talk to our team" path for buyers who outgrow
// Scale; that's enough until the real Enterprise feature set lands.
export const TIERS: TierCard[] = [
  {
    slug: 'starter',
    name: 'Starter',
    tagline: 'Solo agencies + freelancers',
    monthly_usd: 9,
    yearly_usd: 90,
    highlights: [
      '5,000 audit credits per month',
      'All 13 audit tools unlocked',
      'Logo + 1 brand colour',
      'Email + dashboard delivery',
      'Email support (72h)',
    ],
    ctaLabel: 'Get Starter',
    trialCtaLabel: 'Try free for 7 days',
  },
  {
    slug: 'growth',
    name: 'Growth',
    tagline: 'Most agencies start here',
    monthly_usd: 29,
    yearly_usd: 290,
    highlights: [
      '19,000 audit credits per month',
      'All 13 audit tools unlocked',
      'White-label PDF reports',
      'Custom domain + your brand colours',
    ],
    highlight: true,
    ctaLabel: 'Get Growth',
  },
  {
    slug: 'scale',
    name: 'Scale',
    tagline: 'Agencies billing $5K+/mo retainers',
    monthly_usd: 79,
    yearly_usd: 790,
    highlights: [
      '54,500 audit credits per month',
      'Everything in Growth',
      'Priority support',
    ],
    ctaLabel: 'Get Scale',
  },
];

export function formatPrice(usd: number | null): string {
  if (usd === null) return 'Custom';
  if (usd === 0) return '$0';
  return `$${usd}`;
}

// Comment marker for the comparison table: it used to read
// `credits` / `whiteLabel` / `leadDelivery` / `support` as rows. Those
// fixed slots were retired in favour of free-form `highlights: string[]`
// so each tier can list only what it actually offers. The legacy
// PricingComparisonTable file still lives in this folder for historical
// reference; it's not imported anywhere active. If a real feature-grid
// view is needed in the future, rebuild it by zipping the `highlights`
// arrays into rows.
